<template>
  <div class="supplier-tab">
    <div class="filters-section">
      <div class="filters-header">
        <h4>
          <a-icon type="filter" class="filter-icon" />
          {{ $t("all.filtresEtRecherche") }}
        </h4>
      </div>
      <a-row :gutter="[16, 16]" class="align-items-end">
        <a-col :xs="24" :sm="12" :md="10">
          <div class="filter-item">
            <label class="filter-label">
              <a-icon type="search" class="action-icon" />
              {{ $t("action.search") }}
            </label>
            <a-input-search
              v-model="searchText"
              allow-clear
              class="search-input"
              :placeholder="$t('fournisseur.searchPlaceholder')"
              @search="applyFilters"
              @pressEnter="applyFilters"
            />
          </div>
        </a-col>
        <a-col :xs="24" :sm="12" :md="8">
          <div class="filter-item">
            <label class="filter-label">
              <a-icon type="tags" class="action-icon" />
              {{ $t("fournisseur.category") }}
            </label>
            <a-select
              v-model="filterCategory"
              allow-clear
              class="filter-select"
              :placeholder="$t('fournisseur.category')"
            >
              <a-select-option
                v-for="c in categories"
                :key="c._id"
                :value="c._id"
              >
                {{ c.name }}
              </a-select-option>
            </a-select>
          </div>
        </a-col>
        <a-col :xs="24" :sm="24" :md="6">
          <div class="supplier-filter-checkbox-wrap">
            <a-checkbox v-model="showArchived" @change="applyFilters">
              {{ $t("fournisseur.showArchived") }}
            </a-checkbox>
          </div>
        </a-col>
      </a-row>
    </div>

    <div class="table-container">
      <a-table
        row-key="_id"
        :loading="loading"
        :columns="columns"
        :data-source="rows"
        :pagination="tablePagination"
        @change="onTableChange"
        :scroll="{ x: 'max-content' }"
        :custom-row="(record) => ({ on: { click: () => gotoProfile(record) } })"
        :row-class-name="supplierRowClassName"
      >
        <template slot="fullName" slot-scope="text">
          <span class="client-name">{{ text || "—" }}</span>
        </template>
        <template slot="status" slot-scope="text">
          <a-tag :color="text === 'active' ? 'green' : 'default'">
            {{
              text === "active"
                ? $t("fournisseur.active")
                : $t("fournisseur.archived")
            }}
          </a-tag>
        </template>
        <template slot="retenue" slot-scope="text">
          <span class="amount-cell">{{ text != null ? text + "%" : "—" }}</span>
        </template>
        <template slot="categoryName" slot-scope="text, record">
          {{ getCategoryName(record.category) }}
        </template>
        <template slot="accountingClass" slot-scope="text, record">
          <a-tag v-if="record.accountingClass" color="geekblue">
            {{ record.accountingClass }}
          </a-tag>
          <a-button
            v-else
            size="small"
            type="link"
            @click.stop="openAssignModal(record)"
          >
            {{ $t("comptabilite.accountingClassAssign") }}
          </a-button>
        </template>
        <template slot="action" slot-scope="text, record">
          <div class="editable-row-operations" @click.stop>
            <a-button
              type="primary"
              size="small"
              @click.stop="openModal(record)"
            >
              {{ $t("action.modifier") }}
            </a-button>
            <a-button
              v-if="record.status === 'active'"
              type="default"
              size="small"
              class="ml-2"
              @click.stop
            >
              <a-popconfirm
                :title="$t('fournisseur.confirmArchive')"
                @confirm="() => archive(record._id)"
              >
                <a>{{ $t("fournisseur.archiver") }}</a>
              </a-popconfirm>
            </a-button>
          </div>
        </template>
      </a-table>
    </div>

    <a-modal
      :visible="modalVisible"
      :title="editingId ? $t('action.modifier') : $t('action.ajouter')"
      :confirm-loading="saving"
      :width="780"
      @ok="onModalOk"
      @cancel="closeModal"
      destroy-on-close
    >
      <a-form :form="form" layout="vertical">
        <div class="row">
          <div class="col-md-6">
            <a-form-item :label="$t('fournisseur.companyName')">
              <a-input
                v-decorator="[
                  'fullName',
                  {
                    rules: [
                      {
                        required: true,
                        whitespace: true,
                        message: $t('requis.nom'),
                      },
                    ],
                  },
                ]"
              />
            </a-form-item>
          </div>
          <div class="col-md-6">
            <a-form-item :label="$t('fournisseur.codeTVA')">
              <a-input v-decorator="['codeTVA', { initialValue: '' }]" />
            </a-form-item>
          </div>
        </div>
        <div class="row">
          <div class="col-md-12">
            <a-form-item :label="$t('fournisseur.subCategory')">
              <a-select
                v-decorator="['subCategory']"
                show-search
                :filter-option="subcategoryFilterOption"
                allow-clear
              >
                <a-select-opt-group
                  v-for="g in subcategoryGroups"
                  :key="g.categoryId"
                >
                  <span slot="label" class="depense-subcategory-group-label">
                    <span class="depense-subcategory-group-name">{{
                      g.categoryName
                    }}</span>
                    <a-tag
                      v-if="g.expenseKind"
                      :color="expenseKindTagColor(g.expenseKind)"
                      class="depense-subcategory-expense-kind-tag"
                    >
                      {{ $t(expenseKindTagLabelKey(g.expenseKind)) }}
                    </a-tag>
                  </span>
                  <a-select-option
                    v-for="s in g.subs"
                    :key="s._id"
                    :value="s._id"
                  >
                    {{ s.name }}
                  </a-select-option>
                </a-select-opt-group>
              </a-select>
            </a-form-item>
          </div>
        </div>
        <div class="row">
          <div class="col-md-4">
            <a-form-item :label="$t('etablissement.phone')">
              <phone-number-input
                default-country-code="TN"
                v-decorator="['phone']"
              />
            </a-form-item>
          </div>
          <div class="col-md-4">
            <a-form-item :label="$t('comptabilite.email')">
              <a-input
                type="email"
                v-decorator="[
                  'email',
                  {
                    initialValue: '',
                    rules: [{ type: 'email', message: $t('requis.email') }],
                  },
                ]"
              />
            </a-form-item>
          </div>
          <div class="col-md-4">
            <a-form-item :label="$t('fournisseur.retenue')">
              <a-input-number
                v-decorator="[
                  'retenue',
                  {
                    initialValue: 0,
                    rules: [
                      { type: 'number', min: 0, max: 100, message: '0 - 100' },
                    ],
                  },
                ]"
                :min="0"
                :max="100"
                style="width: 100%"
                :formatter="(v) => v + '%'"
                :parser="(v) => v.replace('%', '')"
              />
            </a-form-item>
          </div>
        </div>
        <div class="row">
          <div class="col-md-6">
            <a-form-item :label="$t('etablissement.adress')">
              <a-input v-decorator="['adress', { initialValue: '' }]" />
            </a-form-item>
          </div>
          <div class="col-md-6">
            <a-form-item :label="$t('fournisseur.website')">
              <a-input
                v-decorator="[
                  'website',
                  {
                    initialValue: '',
                    rules: [
                      {
                        pattern:
                          /^$|^(https?:\/\/)?(([a-zA-Z0-9-]+\.)+[a-zA-Z]{2,})(:\d{2,5})?(\/.*)?$/,
                        message: `${$t('fournisseur.website')} invalide`,
                      },
                    ],
                  },
                ]"
              />
            </a-form-item>
          </div>
        </div>
        <div v-if="!editingId" class="row">
          <div class="col-md-12">
            <a-form-item :label="$t('comptabilite.accountingClassReference')">
              <plan-comptable-select
                v-decorator="['accountingParentClass']"
              />
              <div class="text-muted small">
                {{ $t("comptabilite.accountingClassChildHint") }}
              </div>
            </a-form-item>
          </div>
        </div>
        <div v-else-if="editingClass || editingParentClass" class="row">
          <div class="col-md-6">
            <a-form-item :label="$t('comptabilite.accountingClass')">
              <a-input :value="editingClass || '—'" disabled />
            </a-form-item>
          </div>
          <div class="col-md-6">
            <a-form-item :label="$t('comptabilite.accountingParentClass')">
              <a-input :value="editingParentClass || '—'" disabled />
            </a-form-item>
          </div>
        </div>
        <a-form-item :label="$t('comptabilite.description')">
          <a-textarea
            v-decorator="['description', { initialValue: '' }]"
            :rows="2"
          />
        </a-form-item>
      </a-form>
    </a-modal>

    <a-modal
      :visible="assignVisible"
      :title="$t('comptabilite.accountingClassAssign')"
      :confirm-loading="assignSaving"
      :width="520"
      @ok="onAssignOk"
      @cancel="closeAssignModal"
      destroy-on-close
    >
      <a-form layout="vertical">
        <a-form-item :label="$t('comptabilite.accountingClassReference')">
          <plan-comptable-select v-model="assignClass" />
          <div class="text-muted small">
            {{ $t("comptabilite.accountingClassChildHint") }}
          </div>
        </a-form-item>
      </a-form>
    </a-modal>
  </div>
</template>

<script>
import apiClient from "@/services/axios";
import { showApiError } from "@/utilities/showApiError";
import {
  buildSubcategoryGroups,
  categoryIdForSubcategory,
  createSubcategoryFilter,
} from "@/utilities/depense/depenseSubcategories";
import {
  expenseKindTagColor,
  expenseKindTagLabelKey,
} from "@/utilities/depense/expenseKindUi";
import PlanComptableSelect from "@/views/compV3/decaissement/components/PlanComptableSelect.vue";

export default {
  name: "SupplierTab",
  components: { PlanComptableSelect },
  beforeMount() {
    this.form = this.$form.createForm(this);
  },
  data() {
    return {
      loading: false,
      rows: [],
      pagination: { current: 1, pageSize: 20, total: 0 },
      categories: [],
      subcategories: [],
      searchText: "",
      filterCategory: undefined,
      showArchived: false,
      modalVisible: false,
      saving: false,
      editingId: null,
      editingClass: "",
      editingParentClass: "",
      assignVisible: false,
      assignSaving: false,
      assignTarget: null,
      assignClass: "",
    };
  },
  computed: {
    tablePagination() {
      return {
        current: this.pagination.current,
        pageSize: this.pagination.pageSize,
        total: this.pagination.total,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50"],
        showTotal: (t) => `${t}`,
      };
    },
    subcategoryGroups() {
      return buildSubcategoryGroups(this.categories, this.subcategories);
    },
    subcategoryFilterOption() {
      return createSubcategoryFilter(this.categories, this.subcategories);
    },
    columns() {
      return [
        {
          title: this.$t("fournisseur.companyName"),
          dataIndex: "fullName",
          key: "fullName",
          sorter: (a, b) => (a.fullName || "").localeCompare(b.fullName || ""),
          scopedSlots: { customRender: "fullName" },
        },
        {
          title: this.$t("fournisseur.codeTVA"),
          dataIndex: "codeTVA",
          key: "codeTVA",
        },
        {
          title: this.$t("fournisseur.category"),
          key: "categoryName",
          scopedSlots: { customRender: "categoryName" },
        },
        {
          title: this.$t("etablissement.phone"),
          dataIndex: "phone",
          key: "phone",
        },
        {
          title: this.$t("fournisseur.retenue"),
          dataIndex: "retenue",
          key: "retenue",
          scopedSlots: { customRender: "retenue" },
        },
        {
          title: this.$t("comptabilite.accountingClass"),
          key: "accountingClass",
          scopedSlots: { customRender: "accountingClass" },
        },
        {
          title: this.$t("action.status"),
          dataIndex: "status",
          key: "status",
          scopedSlots: { customRender: "status" },
        },
        {
          title: this.$t("action.actions"),
          key: "action",
          fixed: "right",
          scopedSlots: { customRender: "action" },
        },
      ];
    },
  },
  async created() {
    await this.loadAll();
  },
  methods: {
    expenseKindTagColor,
    expenseKindTagLabelKey,
    /** Called from parent page header ("Ajouter"). */
    openModal(record) {
      this.editingId = record ? record._id : null;
      this.editingClass = (record && record.accountingClass) || "";
      this.editingParentClass = (record && record.accountingParentClass) || "";
      this.modalVisible = true;
      this.$nextTick(() => {
        if (record) {
          this.form.setFieldsValue({
            fullName: record.fullName,
            codeTVA: record.codeTVA || "",
            subCategory: record.subCategory || undefined,
            phone: record.phone || "",
            email: record.email || "",
            retenue: record.retenue || 0,
            adress: record.adress || "",
            website: record.website || "",
            description: record.description || "",
          });
        } else {
          this.form.resetFields();
        }
      });
    },
    openAssignModal(record) {
      this.assignTarget = record;
      this.assignClass = "";
      this.assignVisible = true;
    },
    closeAssignModal() {
      this.assignVisible = false;
      this.assignTarget = null;
      this.assignClass = "";
    },
    onAssignOk() {
      if (!this.assignTarget || !this.assignClass) {
        this.$message.warning(
          this.$t("comptabilite.accountingClassParentMissingShort")
        );
        return;
      }
      this.assignSaving = true;
      apiClient
        .patch(`/providers/${this.assignTarget._id}/assign-class`, {
          data: { accountingParentClass: this.assignClass },
        })
        .then(() => {
          this.$message.success(this.$t("success.operationEffectue"));
          this.closeAssignModal();
          this.fetchSuppliers();
        })
        .catch((err) => showApiError(this, err))
        .finally(() => {
          this.assignSaving = false;
        });
    },
    supplierRowClassName(record, index) {
      return index % 2 === 0 ? "table-row-even" : "table-row-odd";
    },
    buildSupplierParams() {
      const p = {
        page: this.pagination.current,
        pageSize: this.pagination.pageSize,
      };
      p.status = this.showArchived ? "all" : "active";
      if (this.filterCategory) p.category = this.filterCategory;
      if (this.searchText) p.search = this.searchText;
      return p;
    },
    async loadAll() {
      this.loading = true;
      try {
        const params = this.buildSupplierParams();
        const [providersRes, catRes, subCatRes] = await Promise.all([
          apiClient.get("/compV3/decaissement/suppliers/list", { params }),
          apiClient.get("/compV3/depense/categories"),
          apiClient.get("/compV3/depense/subcategories"),
        ]);
        const body = providersRes.data || {};
        this.rows = (body.data || []).map((e) => ({ ...e, key: e._id }));
        this.pagination.total = body.total || 0;
        this.pagination.current = body.page || 1;
        this.categories = catRes.data || [];
        this.subcategories = subCatRes.data || [];
      } catch (e) {
        this.$message.error(this.$t("warning.connectionProblem"));
      } finally {
        this.loading = false;
      }
    },
    async fetchSuppliers() {
      this.loading = true;
      try {
        const params = this.buildSupplierParams();
        const res = await apiClient.get("/compV3/decaissement/suppliers/list", {
          params,
        });
        const body = res.data || {};
        this.rows = (body.data || []).map((e) => ({ ...e, key: e._id }));
        this.pagination.total = body.total || 0;
        this.pagination.current = body.page || 1;
      } catch {
        this.$message.error(this.$t("warning.connectionProblem"));
      } finally {
        this.loading = false;
      }
    },
    applyFilters() {
      this.pagination.current = 1;
      this.fetchSuppliers();
    },
    onTableChange(pag) {
      this.pagination.current = pag.current;
      this.pagination.pageSize = pag.pageSize;
      this.fetchSuppliers();
    },
    getCategoryName(catId) {
      if (!catId) return "—";
      const cat = this.categories.find((c) => c._id === catId);
      return cat ? cat.name : "—";
    },
    closeModal() {
      this.modalVisible = false;
      this.editingId = null;
      this.editingClass = "";
      this.editingParentClass = "";
      this.form.resetFields();
    },
    onModalOk(e) {
      e.preventDefault();
      this.form.validateFields((err, values) => {
        if (err) return;
        const phone = (values.phone || "").trim();
        if (phone) {
          const digits = phone.replace(/\D/g, "");
          if (phone.startsWith("+216") && digits.length !== 11) {
            this.$message.error(this.$t("requis.telephone"));
            return;
          }
          if (phone.startsWith("+254") && digits.length !== 13) {
            this.$message.error(this.$t("requis.telephoneKE"));
            return;
          }
        }
        this.saving = true;
        const category = categoryIdForSubcategory(
          this.subcategories,
          values.subCategory
        );
        const data = { ...values, category };
        if (this.editingId) {
          delete data.accountingParentClass;
        } else if (!data.accountingParentClass) {
          delete data.accountingParentClass;
        }
        const payload = { data };
        const req = this.editingId
          ? apiClient.patch(`/providers/${this.editingId}`, payload)
          : apiClient.put("/providers", payload);
        req
          .then(() => {
            this.$message.success(
              this.editingId
                ? this.$t("success.fournisseurMSA")
                : this.$t("success.fournisseurAjout")
            );
            this.closeModal();
            this.loadAll();
          })
          .catch((e) => {
            showApiError(this, e);
          })
          .finally(() => {
            this.saving = false;
          });
      });
    },
    archive(id) {
      apiClient
        .patch(`/providers/${id}`, { data: { status: "inactive" } })
        .then(() => {
          this.$message.success(this.$t("fournisseur.supplierArchived"));
          this.loadAll();
        })
        .catch(() => this.$message.error(this.$t("warning.connectionProblem")));
    },
    gotoProfile(record) {
      this.$router.push({ path: `/decaissement/fournisseur/${record._id}` });
    },
  },
};
</script>

<style scoped>
.ant-table-tbody tr {
  cursor: pointer;
}
.supplier-filter-checkbox-wrap {
  padding-top: 4px;
  min-height: 40px;
  display: flex;
  align-items: center;
}
</style>

<style>
@import "~@/views/compV3/common.css";
</style>
